import { Box } from "@mui/material";
import { Icon } from "components/Icons";
import { useBreak } from "hooks/useBreak";
import PropTypes from "prop-types";
import React from "react";
import { getFocus, useGet } from "state/jotai";
import { active_project } from "state/store.global";
import { _project } from "state/store.projects";
import styled from "styled-components/macro";

function Highlights({ items }) {
  const { pin, pid } = useGet(active_project);
  const highlightsLabel = getFocus(_project, "Highlights")?.[pin]?.[pid] ?? [];
  const isMobile = useBreak("md_dn");

  // ✅ Step 1: Filter out invalid and duplicate items
  const filteredItems = React.useMemo(() => {
    const seen = new Set();
    return (
      items
        ?.filter((item) => item?.value) // only items with value
        ?.filter((item) => {
          const key = item.property?.toLowerCase?.();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        }) ?? []
    );
  }, [items]);
  
  return (
    <HighlightBox className="box" isMobile={isMobile}>
      <Box
        sx={{
          display: "flex",
          gap: "48px",
          width: "271px",
          mx: { xs: "auto", lg: "90px" },
          justifyContent: { xs: "space-between", lg: "center" },
        }}
      >
        {/* Even index items */}
        <Items className="item-1">
          {filteredItems
            ?.slice(0, 4)
            ?.map((item, i) =>
              i % 2 === 0 ? (
                <div key={`item1-${i}`}>
                  <HighlightItem item={item} />
                </div>
              ) : null
            )}
        </Items>

        {/* Odd index items */}
        <Items className="item-2">
          {filteredItems
            ?.slice(0, 4)
            ?.map((item, i) =>
              i % 2 !== 0 ? (
                <div key={`item2-${i}`}>
                  <HighlightItem item={item} />
                </div>
              ) : null
            )}
        </Items>
      </Box>
    </HighlightBox>
  );
}

Highlights.propTypes = {
  items: PropTypes.array.isRequired,
};

function HighlightItem({ item }) {
  const { pin, pid } = useGet(active_project);
  const highlightsLabel = getFocus(_project, "Highlights")?.[pin]?.[pid] ?? [];

  const label =
    highlightsLabel?.find((h) => h.Property === item.property)?.Label ?? "";

  return (
    <Box
      sx={{
        display: "flex",
        lineHeight: 1.3,
        gap: "8px",
        alignItems: "center",
        minHeight: "50px",
        color: "#004337",
      }}
    >
      <Box sx={{ display: "flex" }}>
        <Icon
          icon={item?.icon}
          isFaIcon={item?.icon === "compas"}
          size={item?.icon === "compas" ? "xl" : ""}
          sx={{
            width: "25px",
            height: "25px",
          }}
        />
      </Box>
      <div>
        {label && (
          <div className="paragraph1-light" style={{ whiteSpace: "nowrap" }}>
            {label}
          </div>
        )}
        <div className="h4" style={{ whiteSpace: "nowrap" }}>
          {item?.value} {item?.postfix}
        </div>
      </div>
    </Box>
  );
}

HighlightItem.propTypes = {
  item: PropTypes.object.isRequired,
};

export default Highlights;

const HighlightBox = styled.div``;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  max-width: 50%;
`;
